
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

export const MileageSystemFixer: React.FC = () => {
  const [isFixing, setIsFixing] = useState(false);
  const [fixResult, setFixResult] = useState<{
    success: boolean;
    message: string;
    updatedCount?: number;
  } | null>(null);

  const fixMileageSystem = async () => {
    setIsFixing(true);
    setFixResult(null);

    try {
      const response = await fetch('/api/fix-mileage-calculation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFixResult({
          success: true,
          message: data.message,
          updatedCount: data.updatedCount
        });
        toast.success('Система пробега успешно исправлена');
      } else {
        throw new Error(data.error || 'Ошибка при исправлении системы пробега');
      }
    } catch (err: any) {
      console.error('Ошибка при исправлении системы пробега:', err);
      setFixResult({
        success: false,
        message: err.message || 'Неизвестная ошибка'
      });
      toast.error('Не удалось исправить систему пробега');
    } finally {
      setIsFixing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-yellow-500" />
          Исправление системы пробега
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="text-sm text-muted-foreground">
          Если у курьеров отображается 0 км пробега, используйте эту функцию для 
          исправления расчетов и создания автоматических триггеров обновления.
        </div>

        <Button
          onClick={fixMileageSystem}
          disabled={isFixing}
          className="w-full"
        >
          {isFixing ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Исправляем систему...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Исправить систему пробега
            </>
          )}
        </Button>

        {fixResult && (
          <div className={`p-3 rounded-lg border ${
            fixResult.success 
              ? 'bg-green-50 border-green-200 text-green-800' 
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-center gap-2">
              {fixResult.success ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              <span className="font-medium">
                {fixResult.success ? 'Успешно' : 'Ошибка'}
              </span>
            </div>
            <div className="mt-1 text-sm">
              {fixResult.message}
            </div>
            {fixResult.updatedCount !== undefined && (
              <div className="mt-1 text-xs">
                Обновлено аренд: {fixResult.updatedCount}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
